import { ReactNode } from "react";
import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { Link, Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { GovSettingsOn, useGovernanceSettings } from "../../../utils/icGovernance";
import { GOV_TABS, PRIMARY, clientLabel, vendorLabel, visibleTabs } from "./governanceStatus";
import { INK, MONO, MUTED } from "./registers/cells";

/**
 * /dashboard/governance — project header + the workbook tab row around the
 * register routes (`<Outlet/>`). Admins only: the server answers
 * `{enabled:false}` to everyone else. Registers read the loaded settings with
 * `useGovernance()` instead of fetching them again. Item detail and the weekly
 * report page sit outside this layout (their own routes).
 * Contract: thrive_server/claude_doc/governance/CONTRACT.md.
 */

export interface GovernanceContextValue {
  settings: GovSettingsOn;
}

/** The governance settings inside a register (only rendered when enabled). */
export const useGovernance = () => useOutletContext<GovernanceContextValue>();

const Page = ({ children }: { children: ReactNode }) => (
  <Box sx={{ padding: { xs: "16px", sm: "20px" }, textAlign: "left", minWidth: 0 }}>{children}</Box>
);

const GovernanceLayout = () => {
  const { settings, loading, error } = useGovernanceSettings();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <Page>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      </Page>
    );
  }
  if (error) {
    return (
      <Page>
        <Alert severity="error">{error}</Alert>
      </Page>
    );
  }
  if (!settings.enabled) {
    return (
      <Page>
        <Alert severity="info">
          {settings.reason === "not_configured"
            ? "Project governance is not configured on the server yet (IC_TICKET_API_KEY is not set)."
            : "Project governance is available to administrators only."}
        </Alert>
      </Page>
    );
  }

  const tabs = visibleTabs(settings);
  const current = GOV_TABS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));
  // A tab whose module is off (typed URL, or the Tracker as the default
  // landing tab) goes to the first tab the project uses.
  if (current && !tabs.some((t) => t.key === current.key) && tabs.length > 0) {
    return <Navigate to={tabs[0].path} replace />;
  }

  const context: GovernanceContextValue = { settings };

  return (
    <Page>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED }}
        >
          Project governance
        </Typography>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: INK, mt: 0.25 }}>
          Thrive
          <Box
            component="span"
            sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: MUTED, ml: 1 }}
          >
            {settings.projectKey}
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>
          Client: {clientLabel(settings)} · Vendor: {vendorLabel(settings)}
        </Typography>
      </Box>

      <Tabs
        value={current?.key ?? false}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Project sections"
        sx={{
          mb: 2.5,
          borderBottom: "1px solid #d9dee6",
          minHeight: 44,
          "& .MuiTabs-indicator": { backgroundColor: PRIMARY, height: 3 },
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={t.label}
            component={Link}
            to={t.path}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              color: "#3f4753",
              "&.Mui-selected": { color: PRIMARY },
            }}
          />
        ))}
      </Tabs>

      <Outlet context={context} />
    </Page>
  );
};

export default GovernanceLayout;
